import { type CSSProperties, type ReactNode, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Plus } from "lucide-react";

import { AppSidebar, type AppSidebarProps } from "@/components/layout/AppSidebar";
import {
  NewBudgetModal,
  type NewBudgetModalProps,
  type NewBudgetSelection,
} from "@/components/layout/NewBudgetModal";
import OrzioLogo from "@/components/brand/OrzioLogo";
import LanguageToggle from "@/components/LanguageToggle";
import { Button } from "@/components/ui/button";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";

const APP_SIDEBAR_WIDTH = "220px";
const DEFAULT_SIDEBAR_EXCLUDED_PATHS = ["/", "/auth"];

export interface AppLayoutProps {
  children: ReactNode;
  header?: ReactNode;
  sidebarProps?: Omit<AppSidebarProps, "onNewBudgetClick">;
  className?: string;
  contentClassName?: string;
  showSidebar?: boolean;
  sidebarExcludedPaths?: string[];
  onNewBudgetSelect?: (selection: NewBudgetSelection) => void;
}

export function AppLayout({
  children,
  header,
  sidebarProps,
  className,
  contentClassName,
  showSidebar,
  sidebarExcludedPaths = DEFAULT_SIDEBAR_EXCLUDED_PATHS,
  onNewBudgetSelect,
}: AppLayoutProps) {
  const { pathname } = useLocation();
  const { t } = useLanguage();
  const [isNewBudgetOpen, setIsNewBudgetOpen] = useState(false);
  const shouldShowSidebar =
    showSidebar ?? !sidebarExcludedPaths.some((path) => pathname === path || pathname.startsWith(`${path}/`));
  const shouldShowDesktopUtilityBar =
    !header &&
    !pathname.startsWith("/dashboard") &&
    !pathname.startsWith("/budgets") &&
    !pathname.startsWith("/budget/") &&
    pathname !== "/budget/new" &&
    !pathname.startsWith("/settings");

  useEffect(() => {
    if (pathname === "/budget/new") {
      setIsNewBudgetOpen(true);
    }
  }, [pathname]);

  const handleNewBudgetSelect: NewBudgetModalProps["onSelectIntention"] = (selection) => {
    onNewBudgetSelect?.(selection);
  };

  if (!shouldShowSidebar) {
    return (
      <main className={cn("min-h-svh bg-background", className)}>
        <div className={contentClassName}>{children}</div>
      </main>
    );
  }

  return (
    <SidebarProvider
      defaultOpen
      style={
        {
          "--sidebar-width": APP_SIDEBAR_WIDTH,
        } as CSSProperties
      }
    >
      <AppSidebar {...sidebarProps} onNewBudgetClick={() => setIsNewBudgetOpen(true)} />
      <SidebarInset className={cn("min-w-0 bg-background", className)}>
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-background/92 px-4 backdrop-blur md:hidden">
          <SidebarTrigger className="h-9 w-9" aria-label={t("appShell.toggleNavigation")} />
          <OrzioLogo size="sm" animated={false} className="min-w-0" />
          <Button
            type="button"
            size="icon"
            className="ml-auto h-9 w-9"
            onClick={() => setIsNewBudgetOpen(true)}
            aria-label={t("nav.newBudget")}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
          </Button>
        </header>

        {shouldShowDesktopUtilityBar ? (
          <header className="sticky top-0 z-30 hidden h-14 items-center justify-between border-b border-border bg-background/92 px-6 backdrop-blur md:flex lg:px-8">
            <Link to="/dashboard" className="min-w-0">
              <OrzioLogo
                size="sm"
                animated={false}
                className="[&_.orzio-logo__text]:hidden lg:[&_.orzio-logo__text]:inline"
              />
            </Link>
            <div className="flex items-center gap-2">
              <span className="hidden text-xs text-muted-foreground lg:inline">{t("appShell.workspace")}</span>
              <LanguageToggle />
            </div>
          </header>
        ) : null}

        {header ? <div className="border-b border-border bg-background/92 backdrop-blur">{header}</div> : null}

        <main className={cn("min-h-[calc(100svh-3.5rem)] flex-1 px-4 py-5 md:px-6 lg:px-8", contentClassName)}>
          {children}
        </main>
      </SidebarInset>

      <NewBudgetModal
        open={isNewBudgetOpen}
        onOpenChange={setIsNewBudgetOpen}
        onSelectIntention={handleNewBudgetSelect}
      />
    </SidebarProvider>
  );
}
